import { useMemo } from 'react';
import { useRouteMatch } from 'quartz-routing/react-router';
import { MY_SAVED_ITEMS_PAGE_SIZE } from './savedItemsTypes';
import { usePlaylist } from './savedItemsQueries';
import { useMyPlaylists } from '../myPlaylists/myPlaylistsQueries';
import { useHasScope } from '../accounts/accountsQueries';
import { useUserOwnsPlaylist } from './savedItemsQueries';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { usePlaylistProgress } from '../userProgress/userProgressQueries';
import { PLAYLIST_RESULTS_URL_FRAGMENT, PLAYLIST_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT } from '../../routes/academyRoutePaths';
export const useAddToPlaylistOptionList = ({
  page = 1,
  skip = false
}) => useMyPlaylists({
  interactionTypes: ['CREATED'],
  limit: MY_SAVED_ITEMS_PAGE_SIZE,
  page,
  skip
});

// This hook can be used with a playlist object or a playlistId
export const useIsHubspotPlaylist = playlistOrPlaylistId => {
  const isPlaylist = playlistOrPlaylistId && typeof playlistOrPlaylistId === 'object';
  const playlistId = isPlaylist ? 'id' in playlistOrPlaylistId ? playlistOrPlaylistId.id :
  // @ts-expect-error we're in the process of removing this
  playlistOrPlaylistId.playlistId : playlistOrPlaylistId;
  const {
    playlist: playlistFromQuery
  } = usePlaylist(playlistId, !!(!playlistId || isPlaylist));
  const playlist = isPlaylist ? playlistOrPlaylistId : playlistFromQuery;
  return (playlist === null || playlist === void 0 ? void 0 : playlist.type) === 'LEARNING_PATH';
};
export const useCanUserEditPlaylist = playlistId => {
  const {
    playlist
  } = usePlaylist(playlistId, !playlistId);
  const userIsOwner = useUserOwnsPlaylist(playlist === null || playlist === void 0 ? void 0 : playlist.id);
  const isHubspotPlaylist = useIsHubspotPlaylist(playlistId);
  const {
    hasScope: hasPublicPlaylistsAdminScope
  } = useHasScope('academy-public-playlists-admin');
  if (isHubspotPlaylist && hasPublicPlaylistsAdminScope) return true;
  return userIsOwner;
};
export const usePlaylistItemCardModels = ({
  playlistItems,
  skip,
  skipProgress
}) => {
  const itemsForQuery = useMemo(() => playlistItems.map(({
    itemId,
    type,
    language
  }) => ({
    id: itemId,
    type: type,
    language: readUnhandledEnumCase(language).toLowerCase()
  })).sort((a, b) => a.id - b.id), [playlistItems]);
  const {
    cardModels,
    cardModelsError,
    cardModelsLoading,
    userProgressDataLoading
  } = useLearningContentCardModels({
    items: itemsForQuery,
    skip,
    skipProgress
  });
  return {
    cardModels,
    cardModelsError,
    cardModelsLoading,
    userProgressDataLoading
  };
};
export const usePlaylistRemainingItemsAndProgress = playlistId => {
  const {
    playlistProgress,
    playlistProgressLoading
  } = usePlaylistProgress(playlistId);
  const status = playlistProgress ? playlistProgress.status : null;
  const remainingProgressItems = playlistProgress ? playlistProgress.itemsProgress.filter(item => item.status !== 'COMPLETED') : [];
  const remainingProgressItemCount = remainingProgressItems.length;
  const totalProgressItemCount = playlistProgress ? playlistProgress.itemsProgress.length : 0;
  const progressAmount = status === 'COMPLETED' ? 1 : status === 'IN_PROGRESS' ? (totalProgressItemCount - remainingProgressItemCount) / totalProgressItemCount : 0;
  return {
    remainingProgressItems,
    remainingProgressItemCount,
    totalProgressItemCount,
    progressAmount,
    status,
    playlistProgressLoading
  };
};
export const useIsPlaylistRoute = () => {
  const match = useRouteMatch([PLAYLIST_RESULTS_URL_FRAGMENT, PLAYLIST_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT]);
  return match ? match.isExact : false;
};